import React, { useEffect, useState, useCallback } from 'react';
import {
  RefreshCw,
  Mail,
  Trash2,
  CheckCircle2,
  Clock,
  AlertOctagon,
  Inbox,
  Loader2,
  Copy,
  Sparkles,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { adminApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

const STATUSES = [
  { key: 'open', label: 'Open', Icon: Inbox, tone: 'bg-primary/15 text-primary border-primary/25' },
  { key: 'in_progress', label: 'In progress', Icon: Clock, tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/25' },
  { key: 'resolved', label: 'Resolved', Icon: CheckCircle2, tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/25' },
  { key: 'spam', label: 'Spam', Icon: AlertOctagon, tone: 'bg-destructive/15 text-destructive border-destructive/25' },
];

const fmtDate = (s) => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch (_e) {
    return s;
  }
};

export function SupportTicketsPanel() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('all');
  const [emailAvailable, setEmailAvailable] = useState(true);
  const [notifyTo, setNotifyTo] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const data = await adminApi.listSupportTickets(params);
      setItems(data?.items || []);
      setCounts(data?.by_status || {});
      setEmailAvailable(!!data?.email_available);
      setNotifyTo(data?.notify_to || '');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load support tickets'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openTicket = (t) => {
    setSelected(t);
    setAdminNote(t.admin_note || '');
  };

  const saveStatus = async (status) => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await adminApi.updateSupportTicket(selected.id, {
        status,
        admin_note: adminNote,
      });
      toast.success(`Marked as ${status.replace('_', ' ')}`);
      setSelected(r.ticket || { ...selected, status, admin_note: adminNote });
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not update ticket'));
    } finally {
      setSaving(false);
    }
  };

  const saveNote = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await adminApi.updateSupportTicket(selected.id, {
        status: selected.status,
        admin_note: adminNote,
      });
      toast.success('Note saved');
      setSelected(r.ticket || selected);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not save note'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await adminApi.deleteSupportTicket(toDelete.id);
      toast.success('Ticket deleted');
      setToDelete(null);
      if (selected?.id === toDelete.id) setSelected(null);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not delete ticket'));
    }
  };

  const copyEmail = (e) => {
    navigator.clipboard?.writeText(e).then(() => toast.success(`Copied ${e}`));
  };

  return (
    <section data-testid="admin-support-tickets-panel" className="space-y-5">
      {/* Header */}
      <Card className="rounded-2xl border border-border/70 bg-card/80 backdrop-blur">
        <CardContent className="p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">Admin &middot; Support</p>
            <h2 className="mt-1 font-display font-semibold text-2xl sm:text-3xl tracking-[-0.02em]">
              Inbound tickets
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Notifications go to <strong className="text-foreground">{notifyTo || 'saurabh@dotconverse.com'}</strong>.{' '}
              {emailAvailable ? (
                <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 size={12} aria-hidden="true" /> Email delivery active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                  <AlertOctagon size={12} aria-hidden="true" /> Email delivery off &mdash; set RESEND_API_KEY to enable.
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-10 w-[160px] rounded-full" data-testid="support-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              className="rounded-full"
              data-testid="support-refresh"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              <span className="ml-1.5">Refresh</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Status counters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="support-counters">
        {STATUSES.map(({ key, label, Icon, tone }) => (
          <Card key={key} className="rounded-2xl border border-border/70 bg-card/70">
            <CardContent className="p-4 flex items-center gap-3">
              <span className={`inline-flex items-center justify-center h-9 w-9 rounded-xl border ${tone}`}>
                <Icon size={16} aria-hidden="true" />
              </span>
              <div>
                <p className="text-[11px] font-mono-edge tracking-[0.18em] uppercase text-muted-foreground">{label}</p>
                <p className="font-display font-semibold text-xl tracking-[-0.02em] tabular-nums">{counts?.[key] ?? 0}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Ticket list */}
      {loading ? (
        <div className="rounded-2xl border border-border/70 bg-card/60 p-10 text-center text-sm text-muted-foreground">
          <Loader2 size={18} className="mx-auto mb-2 animate-spin" /> Loading tickets&hellip;
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center" data-testid="support-empty">
          <Inbox size={22} className="mx-auto text-muted-foreground" aria-hidden="true" />
          <p className="mt-3 text-sm font-medium">No tickets yet.</p>
          <p className="text-[12px] text-muted-foreground">Submissions from /contact will land here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="support-list">
          {items.map((t) => {
            const meta = STATUSES.find((s) => s.key === t.status) || STATUSES[0];
            return (
              <Card
                key={t.id}
                className="rounded-2xl border border-border/70 bg-card/85 backdrop-blur hover:border-primary/30 transition-colors cursor-pointer"
                onClick={() => openTicket(t)}
                data-testid={`support-ticket-${t.id}`}
              >
                <CardContent className="p-5 grid gap-4 sm:grid-cols-[1fr_auto] items-start">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className={`rounded-full text-[10px] px-2.5 py-0.5 ${meta.tone}`}>
                        {meta.label}
                      </Badge>
                      <span className="text-[11px] font-mono-edge tracking-[0.18em] uppercase text-muted-foreground">{t.topic}</span>
                      <span className="text-[11px] text-muted-foreground">&middot; {fmtDate(t.created_at)}</span>
                    </div>
                    <p className="mt-2 font-semibold tracking-[-0.01em] truncate">{t.name}</p>
                    <a
                      href={`mailto:${t.email}`}
                      className="text-[13px] text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t.email}
                    </a>
                    <p className="mt-2 text-sm text-foreground/85 line-clamp-2">{t.message}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={(e) => { e.stopPropagation(); copyEmail(t.email); }}
                      data-testid={`support-copy-${t.id}`}
                    >
                      <Copy size={13} /> <span className="ml-1.5">Copy email</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full text-destructive hover:bg-destructive/10"
                      onClick={(e) => { e.stopPropagation(); setToDelete(t); }}
                      aria-label="Delete ticket"
                      data-testid={`support-delete-${t.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Ticket detail dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="support-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-[-0.02em] text-2xl">{selected.topic}</DialogTitle>
                <DialogDescription>
                  Ticket <code className="px-1.5 py-0.5 rounded bg-secondary text-foreground text-[12px]">{selected.id.slice(0, 8)}</code>{' '}
                  &middot; {fmtDate(selected.created_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="rounded-xl border border-border/60 bg-card/60 p-4">
                  <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">From</p>
                  <p className="mt-1 font-semibold text-sm">{selected.name}</p>
                  <a href={`mailto:${selected.email}?subject=Re%3A%20${encodeURIComponent(selected.topic)}`} className="text-[13px] text-primary hover:underline">
                    {selected.email}
                  </a>
                </div>
                <div className="rounded-xl border border-border/60 bg-card/60 p-4">
                  <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">Message</p>
                  <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                </div>
                <div>
                  <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground mb-1.5">Internal note (admin only)</p>
                  <Textarea
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Notes for the team&hellip;"
                    className="rounded-xl min-h-[90px]"
                    data-testid="support-admin-note"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                  <Sparkles size={12} className="text-primary" aria-hidden="true" />
                  {selected.client_ip ? <span>IP: <code>{selected.client_ip}</code></span> : null}
                  {selected.source ? <span>Source: <code>{selected.source}</code></span> : null}
                </div>
              </div>
              <DialogFooter className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <a
                    href={`mailto:${selected.email}?subject=Re%3A%20${encodeURIComponent(selected.topic)}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 h-10 text-sm font-medium hover:bg-primary/90 transition-colors"
                    data-testid="support-reply-email"
                  >
                    <Send size={14} /> Reply by email
                  </a>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={saveNote} disabled={saving} data-testid="support-save-note">
                    Save note
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  {STATUSES.filter((s) => s.key !== selected.status).map((s) => (
                    <Button
                      key={s.key}
                      variant={s.key === 'spam' ? 'ghost' : 'outline'}
                      size="sm"
                      className={`rounded-full ${s.key === 'spam' ? 'text-destructive hover:bg-destructive/10' : ''}`}
                      onClick={() => saveStatus(s.key)}
                      disabled={saving}
                      data-testid={`support-status-${s.key}`}
                    >
                      Mark {s.label.toLowerCase()}
                    </Button>
                  ))}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this ticket?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently removes the support request from the database. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete ticket
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
